import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivitySummary, SearchCompany } from '@lciapps/lci-core';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import { flatMap, map, share, tap } from 'rxjs/operators';
import { ActivitySummaryModel } from '../../models/activity-summary-model';
import { ActivityImageSizes } from '../../models/image-resize.enum';
import { ActivityAttribute } from '../models/activity-attribute.model';
import {
  ActivityLevel,
  ActivityLevelType
} from '../models/activity-level.model';
import { ActivityParticipant } from '../models/activity-participant.model';
import { Activity, ActivityInvite } from '../models/activity-post.model';
import { ActivityRating } from '../models/activity-rating.model';
import { ActivityTeam } from '../models/activity-team.model';
import { ActivityTimeZone } from '../models/activity-time-zone.model';
import { BlueprintGoal } from '../models/blueprint-goal.model';
import { ActivityBlueprint } from '../models/blueprint.model';
import { PaginationResponse } from '../models/pagination-response';

@Injectable()
export class ActivitiesService {
  public currentActivities: BehaviorSubject<Activity[]> = new BehaviorSubject(
    []
  );
  public sortTable: ReplaySubject<any> = new ReplaySubject(1);
  public toggleValue: ReplaySubject<any> = new ReplaySubject(1);
  public userCompany: SearchCompany = null;

  private activityTypes: any[] = [];

  constructor(private http: HttpClient) {
    /**/
  }

  /**
   * Search activities
   * @param {string} searchText
   * @param {string} filter
   * @param {string} orderBy
   * @param {number} pageNumber
   * @param {number} pageSize
   * @returns {Observable<PaginationResponse<Activity>>}
   */
  public searchActivities(
    searchText: string,
    filter: string,
    orderBy: string,
    pageNumber: number,
    pageSize: number,
    checkOfficer = false
  ): Observable<PaginationResponse<Activity>> {
    const params: any = {
      searchText,
      filter,
      orderBy,
      pageNumber,
      pageSize,
      checkOfficer,
    };
    const getObs = this.http.get(`mobileapi://activities/search?imageDimension=${ActivityImageSizes.image_210x127}`, {
      params,
    }) as Observable<PaginationResponse<Activity>>;
    return getObs.pipe(
      tap((res) => {
        this.currentActivities.next(
          res.results.map((result) => result.document)
        );
      })
    );
  }

  public getActivityTypes(): Observable<any> {
    if (this.activityTypes.length > 0) {
      return of(this.activityTypes);
    } else {
      return this.http.get(`mobileapi://activities/activitytypes`).pipe(
        tap((res) => {
          this.activityTypes = res;
        })
      );
    }
  }

  /**
   * Returns an activity by activityId
   * @param {number} activityId
   * @returns {Observable<Activity>}
   */
  public getActivity(activityId: number): Observable<Activity> {
    const activityObservable = this.http.get(
      `mobileapi://activities/${activityId}?imageDimension=${ActivityImageSizes.image_287x191}`
    ) as Observable<Activity>;
    return activityObservable.pipe(share());
  }

  public getActivityAttributes(
    activityId: number
  ): Observable<Array<ActivityAttribute>> {
    return this.http.get(
      `mobileapi://activities/${activityId}/attributes`
    ) as Observable<ActivityAttribute[]>;
  }

  /**
   * Returns a single blueprint by blueprint id
   * @param {number} blueprintId
   * @returns {Observable<ActivityBlueprint>}
   */
  public getBlueprint(blueprintId: number): Observable<ActivityBlueprint> {
    return this.http.get(`mobileapi://blueprints/${blueprintId}`) as Observable<
      ActivityBlueprint
    >;
  }

  /**
   * Returns and activity invite by id
   * @param {number} activityId
   * @param {number} inviteId
   * @returns {Observable<ActivityInvite>}
   */
  public getActivityInvite(
    activityId: number,
    inviteId: number
  ): Observable<ActivityInvite> {
    return this.http.get(
      `mobileapi://activities/${activityId}/invitees/${inviteId}`
    ) as Observable<ActivityInvite>;
  }

  public getActivityInvitees(activityId: number): Observable<ActivityInvite[]> {
    return this.http.get(
      `mobileapi://activities/${activityId}/invitees`
    ) as Observable<ActivityInvite[]>;
  }

  /**
   * Accepts and activity invite by id
   * @param {number} activityId
   * @param {number} inviteId
   * @returns {Observable<any>}
   */
  public acceptActivityInvite(
    activityId: number,
    inviteId: number
  ): Observable<any> {
    return this.http.post(
      `mobileapi://activities/${activityId}/invitees/${inviteId}/accept`,
      {}
    );
  }

  /**
   * Denies and activity invite by id
   * @param {number} activityId
   * @param {number} inviteId
   * @returns {Observable<any>}
   */
  public denyActivityInvite(
    activityId: number,
    inviteId: number
  ): Observable<any> {
    return this.http.post(
      `mobileapi://activities/${activityId}/invitees/${inviteId}/deny`,
      {}
    );
  }

  /**
   * Sets invite status to maybe for an activity by id
   * @param {number} activityId
   * @param {number} inviteId
   * @returns {Observable<any>}
   */
  public maybeActivityInvite(
    activityId: number,
    inviteId: number
  ): Observable<any> {
    return this.http.post(
      `mobileapi://activities/${activityId}/invitees/${inviteId}/maybe`,
      {}
    );
  }

  /**
   * Returns a list of activity levels for the specified activity id
   * @param {number} activityId
   * @returns {Observable<ActivityLevel[]>}
   */
  public getActivityLevel(activityId: number): Observable<ActivityLevel[]> {
    return this.http.get(
      `mobileapi://activities/${activityId}/levels`
    ) as Observable<ActivityLevel[]>;
  }

  /**
   * Sets the activity level for the specified activity id
   * @param {number} activityId
   * @param {ActivityLevelType} activityLevel
   * @returns {Observable<any>}
   */
  public setActivityLevel(
    activityId: number,
    activityLevel: ActivityLevelType
  ): Observable<any> {
    return this.http.post(`mobileapi://activities/${activityId}/levels`, {
      activityLevel,
    });
  }

  /**
   * Deletes the activity level for the specified activity id
   * @param {number} activityId
   * @param {ActivityLevelType} activityLevel
   * @returns {Observable<any>}
   */
  public deleteActivityLevel(
    activityId: number,
    activityLevel: ActivityLevelType
  ): Observable<any> {
    return this.http.delete(
      `mobileapi://activities/${activityId}/levels/${activityLevel}`
    );
  }

  /**
   * Deletes an activity by activity id
   * @param {number} activityId
   * @returns {Observable<any>}
   */
  public deleteActivity(activityId: number): Observable<any> {
    return this.http.delete(`mobileapi://activities/${activityId}`);
  }

  /**
   * Sets activity as attendance status by id
   * @param {number} activityId
   * @param {string} userId
   * @returns {Observable<any>}
   */
  public attendedActivity(
    activityId: number,
    userId: string,
    status: boolean
  ): Observable<any> {
    if (status) {
      return this.http.post(
        `mobileapi://activities/${activityId}/attended/${userId}`,
        {}
      );
    } else {
      return this.http.post(
        `mobileapi://activities/${activityId}/notattended/${userId}`,
        {}
      );
    }
  }

  /**
   * Gets the rating for the activity id
   * @param {number} activityId
   * @returns {Observable<ActivityRating>}
   */
  public getActivityRatings(activityId: number): Observable<ActivityRating[]> {
    return this.http.get(
      `mobileapi://activities/${activityId}/ratings`
    ) as Observable<ActivityRating[]>;
  }

  /**
   * Posts the activity rating to the specified activity by id
   * @param {number} activityId
   * @param {string} userId
   * @param {number} rating
   * @param {string} ratingComment
   * @returns {Observable<any>}
   */
  public setActivityRatings(
    activityId: number,
    userId: string,
    rating: number,
    ratingComment: string = ''
  ): Observable<any> {
    let timezoneOffset = Math.abs(new Date().getTimezoneOffset().valueOf()) * 60;
    return this.http.post(`mobileapi://activities/${activityId}/ratings?timezoneOffsetInSeconds=${timezoneOffset}`, {
      userId,
      rating,
      ratingComment,
    });
  }

  /**
   * Returns a list of participants for an activity
   * @param {number} activityId
   * @returns {Observable<ActivityParticipant[]>}
   */
  public getActivityParticipants(
    activityId: number
  ): Observable<ActivityParticipant[]> {
    return this.http.get(
      `mobileapi://activities/${activityId}/participants`
    ) as Observable<ActivityParticipant[]>;
  }

  /**
   * Adds a user as an activity participant
   * @param activityId
   * @param userId
   */
  public postActivityParticiupant(
    activityId: number,
    userId: string
  ): Observable<any> {
    return this.http.post(`mobileapi://activities/${activityId}/participants`, {
      userId,
    });
  }

  /**
   * Returns a list of activities for a users summary
   * @param {string} userId
   * @returns {Observable<Activity[]>}
   */
  public getUserActivitiesSummary(
    userId: string,
    startDate: string,
    endDate: string
  ): Observable<Activity[]> {
    const params: any = {};
    if (startDate) {
      params.startDate = startDate;
    }
    if (endDate) {
      params.endDate = endDate;
    }
    return this.http.get(`mobileapi://users/${userId}/activitysummaryweb?imageDimension=${ActivityImageSizes.image_210x127}`, {
      params,
    }) as Observable<Activity[]>;
  }

  /**
   * Load a single activity summary for a user by id
   * @param {string} userId
   * @param {number} activityId
   * @returns {Observable<ActivitySummary>}
   */
  public getUserActivitiesSummaryById(
    userId: string,
    activityId: number
  ): Observable<ActivitySummaryModel> {
    return this.http.get(
      `mobileapi://users/${userId}/activitysummary/${activityId}?imageDimension=${ActivityImageSizes.image_287x191}`
    ) as Observable<ActivitySummaryModel>;
  }

  /**
   * Returns activity invites
   * @param {number} activityId
   * @returns {Observable<any>}
   */
  public getActivitiesTeam(activityId: number): Observable<ActivityTeam> {
    return this.http.get(
      `mobileapi://activities/${activityId}/team`
    ) as Observable<ActivityTeam>;
  }

  /**
   * Returns goals for a blueprint
   * @param {number} blueprintId
   * @returns {Observable<BlueprintGoal[]>}
   */
  public getBlueprintGoals(blueprintId: number): Observable<BlueprintGoal[]> {
    return this.http.get(
      `mobileapi://blueprints/${blueprintId}/goals`
    ) as Observable<BlueprintGoal[]>;
  }

  /**
   * Report an activity by id
   * @param {number} activityId
   * @returns {Observable<any>}
   */
  public reportActivity(activityId: number): Observable<any> {
    let timezoneOffset = Math.abs(new Date().getTimezoneOffset().valueOf()) * 60;
    return this.http.put(`mobileapi://activities/${activityId}/reported?timezoneOffsetInSeconds=${timezoneOffset}`, {});
  }


  public getBlueprintNameWithStatus(blueprintId: number): Observable<any> {
    return this.http.get(`mobileapi://blueprints/${blueprintId}/getBlueprintNameWithStatus`);
  }

  /**
   * Get company
   */
  public getCompanyById(
    companyId: number,
    cache: boolean = false
  ): Observable<any> {
    const params = {
      filter: `(CompanyId eq '${companyId}')`,
    };
    if (cache) {
      if (!this.userCompany) {
        return this.http.get(`mobileapi://companies/search`, { params }).pipe(
          tap((res) => {
            this.userCompany = res;
          })
        );
      } else {
        return of(this.userCompany);
      }
    } else {
      return this.http.get(`mobileapi://companies/search`, { params });
    }
  }

  public userCanReport(userId: string): Observable<boolean> {
    return this.http.get(`mobileapi://users/${userId}/canreport`).pipe(
      map((res: string) => {
        return JSON.parse(res);
      })
    ) as Observable<boolean>;
  }
  // string language, int? activityTypeId = null, int? activityCauseId = null
  public getAllAttributes(
    activityTypeId: number,
    activityCauseId: number
  ): Observable<ActivityAttribute[]> {
    return this.http.get(`apirooturl://languageheader`).pipe(
      flatMap((header: string) => {
        let code = 'en';
        if (header) {
          code = header.split(',')[0];
          if (~code.indexOf('-') && !code.startsWith('zh')) {
            code = code.split('-')[0];
          } else if (code.startsWith('zh')) {
            code = code;
          }
        }
        return this.http.get(
          `mobileapi://activityattributes?language=${code}&activityTypeId=${activityTypeId}&activityCauseId=${activityCauseId}`
        );
      })
    ) as Observable<ActivityAttribute[]>;
  }

  public getAllCurrencies(): Observable<any> {
    return this.http.get(`mobileapi://currencyConversion/currencies`);
  }

  public getLastUsedCurrency(userId): Observable<any> {
    const params = {
      userId: userId,
    };
    return this.http.get(
      `mobileapi://currencyConversion/recentlyUsedCurrencies`,
      { params }
    );
  }

  public getConvertedUSDCurrency(
    date: string,
    currency: string,
    fund: any,
    from: string
  ) {
    let params = new HttpParams();
    if (from === 'fromNewWay') {
      params = params.append('asOfDate', new Date(date).toISOString());
      params = params.append('currencyCode', currency);
      params = params.append('amount', fund);
    } else if (from === 'fromOldWay') {
      params = params.append('asOfDate', date);
      params = params.append('currencyCode', currency);
      params = params.append('amount', fund);
    }
    return this.http.get(`mobileapi://currencyConversion/currencyExchange`, {
      params: params,
    });
  }

  public getTimeZonesList(): Observable<ActivityTimeZone[]> {
    return this.http.get<ActivityTimeZone[]>('mobileapi://activities/activityTimeZones');
  }
}
